import { useCallback, useEffect, useRef, useState } from 'react'

import { useUiStore } from '@/lib/ui/ui-store'

import { chatApi, type ActivityChatSummary, type PrivateConversation } from '../api'
import {
  isActivityMessage,
  isPrivateMessage,
  unreadTotal,
} from '../format'
import { useChatRealtime } from './use-chat-realtime'

export type ChatListPhase = 'loading' | 'ready' | 'error'

export interface ChatListsState {
  activityChats: ActivityChatSummary[]
  privateChats: PrivateConversation[]
  phase: ChatListPhase
  refreshing: boolean
  error: unknown
  refresh: () => void
  setActivityChats: (updater: (items: ActivityChatSummary[]) => ActivityChatSummary[]) => void
  setPrivateChats: (updater: (items: PrivateConversation[]) => PrivateConversation[]) => void
}

export interface ChatListsOptions {
  /**
   * The legacy `/messages` destination only needs private conversations; not
   * requesting the activity list keeps it independent from that endpoint.
   */
  includeActivityChats?: boolean
}

/**
 * Route-local lifecycle for the merged chat list. There is no cache layer
 * (D-042): every refresh replaces both frozen lists, and realtime/polling only
 * schedules a refetch. The shell coral dot is derived from the durable unread
 * counts returned by the API.
 */
export function useChatLists({
  includeActivityChats = true,
}: ChatListsOptions = {}): ChatListsState {
  const [activityChats, setActivityChatsState] = useState<ActivityChatSummary[]>([])
  const [privateChats, setPrivateChatsState] = useState<PrivateConversation[]>([])
  const [phase, setPhase] = useState<ChatListPhase>('loading')
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [request, setRequest] = useState(0)
  const loadedRef = useRef(false)
  const inFlightRef = useRef(false)

  useEffect(() => {
    const controller = new AbortController()
    inFlightRef.current = true
    if (!loadedRef.current) setPhase('loading')

    const activityRequest = includeActivityChats
      ? chatApi.listActivityChats({ signal: controller.signal })
      : Promise.resolve({ items: [] as ActivityChatSummary[] })

    Promise.all([
      activityRequest,
      chatApi.listPrivateConversations({ signal: controller.signal }),
    ])
      .then(([activity, privateList]) => {
        if (controller.signal.aborted) return
        setActivityChatsState(activity.items)
        setPrivateChatsState(privateList.items)
        loadedRef.current = true
        setPhase('ready')
        setRefreshing(false)
        setError(null)
        inFlightRef.current = false
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setError(cause)
        setRefreshing(false)
        inFlightRef.current = false
        if (!loadedRef.current) setPhase('error')
      })

    return () => controller.abort()
  }, [includeActivityChats, request])

  const refresh = useCallback(() => {
    setError(null)
    if (loadedRef.current) setRefreshing(true)
    setRequest((value) => value + 1)
  }, [])

  // Background realtime/focus sync must never flash the inline refresh state.
  const sync = useCallback(() => {
    if (inFlightRef.current) return
    setRequest((value) => value + 1)
  }, [])

  const seenMessagesRef = useRef<Set<string>>(new Set())

  const seenMessage = useCallback((key: string): boolean => {
    const seen = seenMessagesRef.current
    if (seen.has(key)) return true
    seen.add(key)
    if (seen.size > 200) {
      const oldest = seen.values().next().value
      if (oldest) seen.delete(oldest)
    }
    return false
  }, [])

  const applyRealtimeMessage = useCallback(
    (
      kind: 'activity' | 'private',
      id: string,
      message: { id: string; body: string; createdAt: string; mine: boolean },
    ) => {
      if (seenMessage(`${kind}:${message.id}`)) return

      if (kind === 'activity') {
        setActivityChatsState((items) =>
          items.map((chat) =>
            chat.id === id
              ? {
                  ...chat,
                  lastMessage: message.body,
                  lastMessageAt: message.createdAt,
                  unreadCount:
                    chat.unreadCount + (message.mine ? 0 : 1),
                }
              : chat,
          ),
        )
        return
      }

      setPrivateChatsState((items) =>
        items.map((conversation) =>
          conversation.id === id
            ? {
                ...conversation,
                lastMessage: message.body,
                lastMessageAt: message.createdAt,
                unreadCount:
                  conversation.unreadCount + (message.mine ? 0 : 1),
              }
            : conversation,
        ),
      )
    },
    [seenMessage],
  )

  const onActivityMessage = useCallback(
    (activityId: string, raw: unknown): boolean => {
      if (!isActivityMessage(raw)) return false
      applyRealtimeMessage('activity', activityId, raw)
      // The list stays authoritative: the hook also schedules a targeted
      // refetch so unread/pin order reconciles with Postgres.
      return false
    },
    [applyRealtimeMessage],
  )

  const onPrivateMessage = useCallback(
    (conversationId: string, raw: unknown): boolean => {
      if (!isPrivateMessage(raw)) return false
      applyRealtimeMessage('private', conversationId, {
        id: raw.id,
        body: raw.body,
        createdAt: raw.createdAt,
        mine: raw.isMine,
      })
      return false
    },
    [applyRealtimeMessage],
  )

  useChatRealtime({
    onUpdate: sync,
    onActivityMessage,
    onPrivateMessage,
    listenToNotifications: true,
  })

  useEffect(() => {
    if (!includeActivityChats) return
    useUiStore
      .getState()
      .setChatUnread(unreadTotal(activityChats, privateChats) > 0)
  }, [activityChats, includeActivityChats, privateChats])

  const setActivityChats = useCallback(
    (updater: (items: ActivityChatSummary[]) => ActivityChatSummary[]) => {
      setActivityChatsState(updater)
    },
    [],
  )
  const setPrivateChats = useCallback(
    (updater: (items: PrivateConversation[]) => PrivateConversation[]) => {
      setPrivateChatsState(updater)
    },
    [],
  )

  return {
    activityChats,
    privateChats,
    phase,
    refreshing,
    error,
    refresh,
    setActivityChats,
    setPrivateChats,
  }
}
